import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { currentLocale } from '../components/numericFormat';
import { loadParcelsForArea } from '../heatmap/ParcelsAPI';
import { geometryBounds } from '../heatmap/geometryBounds';
import { EMPTY } from './perspectiveValues';
import ParcelMap from './ParcelMap';
import { isOnPlot } from './parcelPlot';
import { PerspectiveScope } from './sitePerspectiveApi';
import { PhaseParcelResult, PhaseParcelRow, setParcelDecision } from './phaseParcelsApi';

/**
 * Deciding which parcels make up the plot, on the map they are on.
 *
 * The estimator works out a plot from where the building stands and what lies around it, and it is
 * right most of the time and wrong in exactly the cases that matter: the yard that belongs to the
 * neighbour, the strip of road that does not belong to anybody, the second parcel behind the house
 * that nobody would guess at. All of those are obvious to a person looking at the map and to nobody
 * else, which is what this is for.
 *
 * Each click is written on its own. There is no Save: the decision is one click, and a dialog that
 * collected clicks would have to explain what happens to them when it is closed. What comes back
 * from each write is the whole plot — outlines and area together — so the figure under the map is
 * never a step behind the picture above it.
 */

interface Properties {
    open: boolean;
    onDismiss: () => void;
    scope: PerspectiveScope | null;
    rows: PhaseParcelRow[];
    siteAreaMin: number | null;
    siteAreaMax: number | null;
    /** The building's outline, for orientation. */
    footprint?: any;
    /** Hands the whole plot back to the page, so table and map stay one answer. */
    onChanged: (result: PhaseParcelResult) => void;
}

/**
 * How far around the plot the neighbours are fetched, in degrees of latitude.
 *
 * About 250 m — far enough that the parcels one might add are in the box, near enough that a city
 * block does not arrive with them. Longitude is stretched by the latitude so the box stays roughly
 * square on the ground rather than becoming a slot at German latitudes.
 */
const MARGIN_DEGREES = 0.00225;

const neighbourhood = (rows: PhaseParcelRow[], footprint: any): any | null => {
    const boxes = [...rows.map((row) => row.geometry), footprint]
        .map(geometryBounds)
        .filter(Boolean) as Array<[[number, number], [number, number]]>;

    if (boxes.length === 0) return null;

    const minLon = Math.min(...boxes.map((box) => box[0][0]));
    const minLat = Math.min(...boxes.map((box) => box[0][1]));
    const maxLon = Math.max(...boxes.map((box) => box[1][0]));
    const maxLat = Math.max(...boxes.map((box) => box[1][1]));

    const lonMargin = MARGIN_DEGREES / Math.max(0.2, Math.cos(((minLat + maxLat) / 2) * (Math.PI / 180)));

    return {
        type: 'Polygon',
        coordinates: [
            [
                [minLon - lonMargin, minLat - MARGIN_DEGREES],
                [maxLon + lonMargin, minLat - MARGIN_DEGREES],
                [maxLon + lonMargin, maxLat + MARGIN_DEGREES],
                [minLon - lonMargin, maxLat + MARGIN_DEGREES],
                [minLon - lonMargin, minLat - MARGIN_DEGREES],
            ],
        ],
    };
};

/**
 * The Flurstücksnummer as a register writes it: numerator, or numerator/denominator.
 *
 * Read off the raw document rather than the model's getter, because the query hands back plain
 * objects — the same composition the server does when it joins an estimate with the cadastre.
 */
const parcelNumberOf = (parcel: any): string | null => {
    const numerator = String(parcel?.zaehler ?? '').trim();
    if (!numerator) return null;

    const denominator = String(parcel?.nenner ?? '').trim();
    return denominator ? `${numerator}/${denominator}` : numerator;
};

const ROLE_KEY: Record<PhaseParcelRow['role'], string> = {
    core: 'portfolioDashboard.parcels.core',
    manual: 'portfolioDashboard.parcels.manual',
    accepted: 'portfolioDashboard.parcels.accepted',
    open: 'portfolioDashboard.parcels.open',
    rejected: 'portfolioDashboard.parcels.rejected',
};

export default function ParcelAssignmentDialog({
    open,
    onDismiss,
    scope,
    rows,
    siteAreaMin,
    siteAreaMax,
    footprint,
    onChanged,
}: Properties) {
    const { t } = useTranslation();
    const [nearby, setNearby] = useState<Array<{ parcelId: string; geometry: any }>>([]);
    const [loadingNearby, setLoadingNearby] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const box = useMemo(() => (open ? neighbourhood(rows, footprint) : null), [open, rows, footprint]);

    /**
     * The neighbours, once.
     *
     * Keyed on the box rather than on the rows: a decision changes the rows and would otherwise
     * re-fetch the neighbourhood on every click, and the cadastre around the plot has not moved.
     */
    const boxKey = useMemo(() => (box ? JSON.stringify(box.coordinates) : ''), [box]);

    useEffect(() => {
        if (!box) {
            setNearby([]);
            return;
        }

        let cancelled = false;
        setLoadingNearby(true);

        loadParcelsForArea(box)
            .then((result) => {
                if (cancelled) return;

                setNearby(
                    result.parcels
                        .map((parcel: any) => ({
                            parcelId: String(parcel?._id ?? ''),
                            geometry: parcel?.geometry,
                            // Carried through for the tooltip: a neighbour is anonymous otherwise,
                            // and "which one is that" is the question one hovers over it to ask.
                            number: parcelNumberOf(parcel),
                            gemarkung: parcel?.gemarkung ?? null,
                            flur: parcel?.flur ?? null,
                            areaM2: Number(parcel?.flaecheM2) || null,
                        }))
                        .filter((parcel) => parcel.parcelId && parcel.geometry),
                );
            })
            .catch((loadError: any) => {
                console.error('Could not load the parcels around the plot', loadError);
                if (!cancelled) setError(loadError?.message ?? t('global.generalError'));
            })
            .finally(() => {
                if (!cancelled) setLoadingNearby(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boxKey]);

    const toggle = useCallback(
        async (parcelId: string, onPlot: boolean) => {
            if (!scope || busy) return;

            setBusy(parcelId);
            setError(null);
            try {
                onChanged(await setParcelDecision(scope, parcelId, onPlot ? 'removed' : 'assigned'));
            } catch (writeError: any) {
                console.error('Could not record the parcel decision', writeError);
                setError(writeError?.message ?? t('global.generalError'));
            } finally {
                setBusy(null);
            }
        },
        [scope, busy, onChanged, t],
    );

    const area = (value: number) => new Intl.NumberFormat(currentLocale()).format(Math.round(value));

    return (
        <Dialog open={open} onOpenChange={(next) => (next ? null : onDismiss())}>
            <DialogContent className="max-h-[92vh] w-[96vw] max-w-[1100px] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('portfolioDashboard.parcels.assignTitle')}</DialogTitle>
                    <DialogDescription>{t('portfolioDashboard.parcels.assignExplanation')}</DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
                    <div className="flex flex-col gap-2">
                        <ParcelMap
                            rows={rows}
                            nearby={nearby}
                            footprint={footprint}
                            height={460}
                            onToggle={toggle}
                            busyParcelId={busy}
                        />

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <Legend colour="#C0392B" label={t('portfolioDashboard.parcels.legendCertain')} />
                            <Legend colour="#D08A17" label={t('portfolioDashboard.parcels.legendOpen')} />
                            <Legend colour="#7A8798" label={t('portfolioDashboard.parcels.legendNearby')} />
                            {loadingNearby ? (
                                <span className="inline-flex items-center gap-1.5">
                                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                                    {t('global.loading')} …
                                </span>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>

                    <div className="flex min-w-0 flex-col gap-2">
                        {/* The area first: it is the figure the rest of the perspective computes
                            with, and the reason anyone is moving parcels around at all. */}
                        <div className="rounded-lg border p-3">
                            <div className="text-xs font-bold uppercase tracking-[0.07em] text-muted-foreground">
                                {t('portfolioDashboard.parcels.totalArea')}
                            </div>
                            <div className="mt-1 text-lg font-semibold tabular-nums">
                                {siteAreaMin === null
                                    ? EMPTY
                                    : siteAreaMax !== null && siteAreaMax > siteAreaMin
                                      ? `${area(siteAreaMin)} – ${area(siteAreaMax)} m²`
                                      : `${area(siteAreaMin)} m²`}
                            </div>
                        </div>

                        <div className="min-w-0 overflow-y-auto rounded-lg border lg:max-h-[420px]">
                            {rows.length === 0 ? (
                                <div className="p-3 text-sm text-muted-foreground">
                                    {t('portfolioDashboard.parcels.none')}
                                </div>
                            ) : (
                                rows.map((row) => (
                                    <button
                                        key={row.parcelId}
                                        type="button"
                                        disabled={!!busy}
                                        title={row.reasons.join(' · ')}
                                        className={cn(
                                            'flex w-full items-baseline justify-between gap-2 border-b px-3 py-2 text-left last:border-b-0',
                                            'hover:bg-accent disabled:opacity-60',
                                        )}
                                        onClick={() => toggle(row.parcelId, isOnPlot(row.role))}
                                    >
                                        <span className="min-w-0">
                                            <span className="block truncate text-sm font-medium">
                                                {row.number ?? EMPTY}
                                            </span>
                                            <span className="block truncate text-xs text-muted-foreground">
                                                {t(ROLE_KEY[row.role])}
                                            </span>
                                        </span>
                                        <span className="shrink-0 text-sm tabular-nums">{area(row.areaM2)} m²</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {error ? (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                ) : (
                    <></>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Legend({ colour, label }: { colour: string; label: string }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: colour }} aria-hidden />
            {label}
        </span>
    );
}
